<script setup lang="ts">
import StepperItemCustom from '@/components/Stepper/StepperItemCustom.vue'
import { usePejer } from '@/Pejer'
import { useQueryClient } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { useCountdown } from '@vueuse/core'
import {
  Building2,
  CircleCheck,
  ClipboardClock,
  Copy,
  Handshake,
  InfoIcon,
  LandmarkIcon,
  ListChecksIcon,
  Search,
  Signature,
} from 'lucide-vue-next'
import DataStateButton from 'pejer/src/components/DataState/DataStateButton.vue'
import DataStatePage from 'pejer/src/components/DataState/DataStatePage.vue'
import HorizontalLineBesideText from 'pejer/src/components/HorizontalLineBesideText.vue'
import Button from 'pejer/src/components/ui/button/Button.vue'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'pejer/src/components/ui/form'
import Input from 'pejer/src/components/ui/input/Input.vue'
import Separator from 'pejer/src/components/ui/separator/Separator.vue'
import { Stepper } from 'pejer/src/components/ui/stepper'
import { toast } from 'pejer/src/components/ui/toast'
import { DataStateLoose } from 'pejer/src/types/types'
import usePejerMutation from 'pejer/src/usePejerMutation'
import usePejerQuery from 'pejer/src/usePejerQuery'
import { useForm } from 'vee-validate'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import z from 'zod'

definePage({
  meta: {
    unauthenticatedAccess: true,
    hideNavbar: true,
  },
})
const route = useRoute()
const pejer = usePejer()
const { data, dataState, isLoading } = usePejerQuery(
  pejer.urls.bankIntegration.getConsent,
  {
    queryKey: ['bank-integration'],
    pathArgs: {
      BankIntegrationConsentID: route.params.BankIntegrationConsentID,
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const currentStep = computed(() => {
  if (!data.value) return null

  switch (data.value.status) {
    case 'Initiated':
    case 'AwaitingOnboarding':
      return 1
    case 'AwaitingSigning':
      return 2
    case 'AwaitingOnboardingApproval':
      return 3
    case 'AwaitingConsent':
      return 4
    case 'Active':
      return 5
    default:
      return 1
  }
})

// const hasSyncedOnboardingStatus = ref(false)
const needsOnboardingSync = computed(() => {
  if (!data.value || dataState.isLoading.value) return false
  // if (hasSyncedOnboardingStatus.value) return false

  return (
    data.value.status === 'AwaitingOnboarding' ||
    data.value.status === 'AwaitingOnboardingApproval' ||
    data.value.status === 'AwaitingSigning' ||
    data.value.status === 'AwaitingConsent' ||
    data.value.status === 'Initiated'
  )
})

// const { mutate: syncConsentStatusMutation, dataState: syncConsentStatusDataState } =
//   usePejerMutation(
//     pejer.urls.bankIntegration.syncConsentStatus,
//     {
//       onSuccess: (data) => {
//         if (data.didSync)
//           queryClient.invalidateQueries({
//             queryKey: ['bank-integration'],
//           })
//       },
//     },
//     {
//       ignoreAuthorization: true,
//     }
//   )

const { mutate: onboardMutation, dataState: onboardDataState } = usePejerMutation(
  pejer.urls.bankIntegration.initiateOnboarding,
  {
    onSuccess: async (data) => {
      if (
        data.onboardingUrl &&
        (data.consentStatusCode === 'AwaitingOnboarding' || data.consentStatusCode === 'Initiated')
      )
        window.location.href = data.onboardingUrl
      else {
        await syncStatus()
        queryClient.invalidateQueries({
          queryKey: ['bank-integration'],
        })
      }
    },
  },
  {
    ignoreAuthorization: true,
  }
)

// We start a continuous polling to sync the consent status with an intervall of 30 seconds as long as we are in a status that requires onboarding.
const POLLING_INTERVAL = 30000
const interval = ref<number | null>(null)
const lastSynced = ref<Date | null>(null)

const syncDataState = ref<DataStateLoose>({
  isLoading: true,
  error: null,
  isError: false,
  isSuccess: false,
  url: pejer.urls.bankIntegration.syncConsentStatus,
})

const syncStatus = async () => {
  syncDataState.value = {
    isLoading: true,
    error: null,
    isError: false,
    isSuccess: false,
    url: pejer.urls.bankIntegration.syncConsentStatus,
  }

  const res = await pejer.fetch(pejer.urls.bankIntegration.syncConsentStatus, {
    pathArgs: {
      BankIntegrationConsentID: route.params.BankIntegrationConsentID,
    },
    ignoreAuthorization: true,
  })

  lastSynced.value = new Date()

  if ('error' in res) {
    syncDataState.value = {
      isLoading: false,
      error: {
        error: res.error,
        statusCode: res.statusCode,
      },
      isError: true,
      isSuccess: false,
      url: pejer.urls.bankIntegration.syncConsentStatus,
    }
    return
  }

  syncDataState.value = {
    isLoading: false,
    error: null,
    isError: false,
    isSuccess: true,
    url: pejer.urls.bankIntegration.syncConsentStatus,
  }

  if ('error' in res === false && res.data.didSync)
    queryClient.invalidateQueries({
      queryKey: ['bank-integration'],
    })
}

// watch(
//   [needsOnboardingSync, data],
//   async ([needsOnboarding, data]) => {
//     if (needsOnboarding) {
//       if (interval.value) {
//         clearInterval(interval.value)
//         interval.value = null
//       }

//       await syncStatus()
//       interval.value = window.setInterval(async () => {
//         await syncStatus()
//       }, POLLING_INTERVAL)
//     } else {
//       if (interval.value) {
//         clearInterval(interval.value)
//         interval.value = null
//       }
//     }
//   },
//   { immediate: true }
// )

const syncCountdown = useCountdown(0, {
  immediate: true,
  onComplete: async () => {
    if (needsOnboardingSync.value) await syncStatus()

    syncCountdown.start(POLLING_INTERVAL / 1000)
  },
})

// watch(
//   needsOnboardingSync,
//   (needsOnboarding) => {
//     if (!needsOnboarding && data.value) return

//     syncConsentStatusMutation({
//       pathArgs: {
//         BankIntegrationConsentID: route.params.BankIntegrationConsentID,
//       },
//     })

//     // hasSyncedOnboardingStatus.value = true
//   },
//   { immediate: true }
// )

const queryClient = useQueryClient()
const { data: banks } = usePejerQuery(
  pejer.urls.bankIntegration.getBanks,
  {
    queryKey: ['banks'],
    pathArgs: {
      BankIntegrationConsentID: route.params.BankIntegrationConsentID,
    },
    enabled: computed(() => currentStep.value === 4),
  },
  {
    ignoreAuthorization: true,
  }
)

const bankPicked = ref<string | null>(null)
const bankSearch = ref('')
const filteredBanks = computed(() => {
  if (!banks.value) return []
  if (!bankSearch.value) return banks.value
  const query = bankSearch.value.toLowerCase()
  return banks.value.filter(
    (bank) => bank.name.toLowerCase().includes(query) || bank.bic.toLowerCase().includes(query)
  )
})

const form = useForm({
  validationSchema: toTypedSchema(
    z.object({
      firstName: z.string().min(1, 'Fornavn er påkrevd'),
      lastName: z.string().min(1, 'Etternavn er påkrevd'),
      email: z.string().email('Ugyldig e-postadresse').min(1, 'E-post er påkrevd'),
    })
  ),
  initialValues: {
    firstName: '',
    lastName: '',
    email: '',
  },
})

const hasInitializedConsentValues = ref(false)
watch(
  data,
  (data) => {
    if (!data || hasInitializedConsentValues.value) return

    form.setValues({
      firstName: data.userFirstName ?? '',
      lastName: data.userLastName ?? '',
      email: data.userEmail ?? '',
    })

    hasInitializedConsentValues.value = true
  },
  { immediate: true }
)

const addConsentMutation = usePejerMutation(
  pejer.urls.bankIntegration.addConsent,
  {
    queryKeys: [['bank-integration']],
    onSuccess: (data) => {
      if (data.redirectUrl) window.location.href = data.redirectUrl
    },
  },
  {
    ignoreAuthorization: true,
  }
)

const addConsent = form.handleSubmit(async () => {
  if (!bankPicked.value) return

  const result = await addConsentMutation.mutateAsync({
    pathArgs: {
      BankIntegrationConsentID: route.params.BankIntegrationConsentID,
    },
    body: {
      Bic: bankPicked.value,
      ...form.values,
    },
  })
})

if (route.query.context === 'corporate' && route.query.instructionId) {
  ;(async () => {
    const res = await pejer.fetch(pejer.urls.bankIntegration.updateConsentStatus, {
      pathArgs: {
        BankIntegrationConsentID: route.params.BankIntegrationConsentID,
      },
      body: {
        InstructionID: route.query.instructionId,
        Status: route.query.scaStatus as 'success' | string,
        Context: route.query.context as string,
      },
      ignoreAuthorization: true,
    })
    queryClient.invalidateQueries({
      queryKey: ['bank-integration'],
    })

    if ('error' in res === false || res.statusCode === 409) {
      // we remove the query params to avoid accidentally re-triggering the update if the user refreshes the page
      const url = new URL(window.location.href)
      url.searchParams.delete('context')
      url.searchParams.delete('instructionId')
      url.searchParams.delete('scaStatus')
      window.history.replaceState({}, document.title, url.toString())
    }
  })()
}

const goToSuccess = () => {
  window.location.href = data.value?.successUrl || '/'
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text)
  toast({
    title: 'Kopiert til utklippstavlen',
    description: text,
  })
}
</script>
<template>
  <div class="flex flex-col gap-4 w-full h-full p-0 items-center">
    <div class="w-full items-center flex flex-col">
      <nav class="py-4 flex justify-between items-end max-w-screen-lg w-full">
        <h1 class="font-bold text-xl text-gray-700 flex items-center gap-2">
          Bankintegrasjon <InfoIcon :size="16" />
        </h1>
        <div
          class="flex py-2 px-3 rounded-md bg-white text-sm text-gray-500 items-center gap-2 font-semibold border-slate-200 border"
        >
          <template v-if="data?.status === 'Active'">
            <span class="w-2 h-2 rounded-full bg-green-500"></span>
            Integrasjon aktiv
          </template>
          <template v-else-if="data?.status === 'Revoked'">
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
            Samtykke trekt
          </template>
          <template v-else-if="data?.status === 'OnboardingFailed'">
            <span class="w-2 h-2 rounded-full bg-red-500"></span>
            Onboarding mislyktes
          </template>
          <template
            v-else-if="data?.status === 'Initiated' || data?.status === 'AwaitingOnboarding'"
          >
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på onboarding
          </template>
          <template v-else-if="data?.status === 'AwaitingSigning'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på signering
          </template>
          <template v-else-if="data?.status === 'AwaitingConsent'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på samtykke
          </template>
          <template v-else-if="data?.status === 'AwaitingOnboardingApproval'">
            <span class="w-2 h-2 rounded-full bg-yellow-500"></span>
            Venter på godkjenning av onboarding
          </template>
          <template v-else>
            <span class="w-2 h-2 rounded-full bg-gray-500"></span>
            Status ukjent
          </template>
        </div>
      </nav>
      <Separator />
    </div>
    <DataStatePage v-bind="dataState">
      <!-- <DataStatePage v-bind="syncDataState"> -->
      <section
        v-if="data?.revokedAt !== null"
        class="max-w-screen-lg w-full flex flex-col justify-center items-center font-bold text-lg text-gray-700 h-full"
      >
        <h1>Samtykke er trekt</h1>
        <p class="text-sm text-gray-500 font-normal">
          Du har trukket tilbake samtykke for bankintegrasjon.
        </p>
      </section>
      <section v-else class="max-w-screen-lg w-full">
        <!-- <p class="text-sm text-gray-500 max-w-[65ch]">
        Bankintegrasjon gjør det mulig for oss å lage tjenester mot bedriftens bankkontoer. Dette gjør
        det mulig for oss å bla. lese transaksjoner, initiere utbetalinger og hente kontoinformasjon.
        For å kunne gjøre dette trenger vi ditt samtykke. Dette samtykket er gyldig i 90 dager, og må
        fornyes etter dette.
      </p> -->
        <!-- <Card v-if="data?.needsOnboardingInitiation">
        <div class="font-bold text-lg">
          Det ser ut som du ikke har fullført onboarding for bankintegrasjon. Klikk
          <a :href="data?.onboardingUrl" target="_blank" class="text-blue-500 underline">her</a>
          for å fullføre onboarding.
        </div>
      </Card> -->
        <div v-if="data?.status !== 'Active'" class="flex flex-col gap-4 w-full items-center">
          <p class="text-sm text-gray-500 flex flex-wrap gap-1 justify-between w-full max-w-[65ch]">
            <span>
              Sist oppdatert:
              {{
                (lastSynced ?? new Date()).toLocaleString('no-NO', {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })
              }}
            </span>
            <span>
              Oppdaterer om
              {{ syncCountdown.remaining.value }} sekunder...
            </span>
          </p>
          <!-- 1. If we do not have an active consent, show the onboarding steps -->
          <Stepper
            orientation="vertical"
            class="mx-auto flex w-full max-w-lg flex-col justify-start gap-0"
            :model-value="currentStep ?? 0"
          >
            <StepperItemCustom :icon="Building2" :step="1">
              <template #title> Onboard bedrift </template>
              <template #description>
                Du vil bli sendt videre til Mastercards onboarding-løsning for å fullføre
                onboardingen for bankintegrasjon.<br />
                <a v-if="data?.onboardingUrl" :href="data.onboardingUrl">
                  <Button class="mt-2" size="sm"> Gå til onboarding </Button>
                </a>
                <DataStateButton
                  v-else
                  :data-state="onboardDataState"
                  class="mt-2"
                  size="sm"
                  @click="
                    onboardMutation({
                      pathArgs: {
                        BankIntegrationConsentID: route.params.BankIntegrationConsentID,
                      },
                    })
                  "
                >
                  Gå til onboarding
                </DataStateButton>
                <br />
                <span class="text-xs text-gray-500">
                  Obs: Mastercard kan noen minutter på å oppdatere status etter at onboarding er
                  fullført, hvis du akkurat har fullført anbefaler vi å vente noen minutter.
                </span>
              </template>
            </StepperItemCustom>
            <StepperItemCustom :icon="Signature" :step="2">
              <template #title> Signering </template>
              <template #description>
                Signer avtalen med Mastercard. En mail fra Mastercard blir sendt til den som skal
                signere.
                <template v-if="currentStep === 2">
                  <template v-if="data?.signingUrl">
                    <br />
                    Signatur kreves av: {{ data.signerName }} <br />
                    <a :href="data.signingUrl">
                      <Button class="mt-2" size="sm"> Gå til signering </Button>
                    </a>
                    eller kopier linken for å sende til vedkommende:
                    <div class="mt-2 p-2 bg-gray-50 rounded-md flex items-center gap-2">
                      <span class="text-sm text-gray-500">{{ data.signingUrl }}</span>
                      <Button size="sm" variant="outline" @click="copyToClipboard(data.signingUrl)">
                        <Copy :size="16" /> Kopier
                      </Button>
                    </div>
                  </template>
                  <template v-else>
                    <br />
                    Venter på at signering skal initieres. Dette kan ta noen minutter, en epost vil
                    bli sendt fra Mastercard til den som skal signere når det er klart.
                  </template>
                </template>
              </template>
            </StepperItemCustom>
            <StepperItemCustom :icon="ClipboardClock" :step="3">
              <template #title> Venter godkjennelse </template>
              <template #description> Venter på godkjenning av Mastercard. </template>
            </StepperItemCustom>
            <StepperItemCustom :icon="Handshake" :step="4" :isLast="true">
              <template #title> Gi samtykke </template>
              <template #description>
                Du vil bli bedt om å gi samtykke til bankintegrasjon i banken for å fullføre
                prosessen. Her kan du velge hvilke bankkontoer du ønsker å gi samtykke for.
                <!-- <div v-if="banks" class="mt-4 mb-2">
                  <div
                    v-for="consent in data?.corporateConsents"
                    :key="consent.redirectUrl"
                    class="p-2 border border-slate-200 rounded-md mb-2"
                  >
                    <div class="flex items-center gap-2 justify-between">
                      <span class="font-semibold">{{ consent.bankName }}</span>
                      <span class="flex items-center gap-1 text-xs text-green-500">
                        Aktivt samtykke
                        <CheckCircle2 :size="16" class="text-green-500" />
                      </span>
                    </div>
                  </div>
                </div> -->
                <div
                  v-if="banks"
                  class="mt-2 mb-4 p-4 bg-gray-50 rounded-md relative border border-slate-200"
                >
                  <form class="flex flex-col gap-4" @submit.prevent="addConsent">
                    <div class="flex flex-col gap-4">
                      <h2 class="text-lg font-semibold">
                        Opplysninger om personen som skal gi samtykke
                      </h2>
                      <p class="text-xs">
                        Opplysningene nedenfor må tilhøre personen som skal logge inn i banken og gi
                        samtykke. Personen må ha tilgang til bedriftens nettbank, inkludert
                        rettigheter til å se og opprette betalinger. Dersom dette ikke er deg, ber
                        vi deg sende lenken videre til riktig person, slik at vedkommende kan fylle
                        inn sine egne opplysninger og gi samtykke.
                      </p>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <FormField v-slot="{ componentField }" name="firstName">
                          <FormItem>
                            <FormLabel class="text-secondaryBlack">Fornavn</FormLabel>
                            <FormControl>
                              <Input
                                class="bg-white"
                                type="text"
                                placeholder="Ola"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>

                        <FormField v-slot="{ componentField }" name="lastName">
                          <FormItem>
                            <FormLabel class="text-secondaryBlack">Etternavn</FormLabel>
                            <FormControl>
                              <Input
                                class="bg-white"
                                type="text"
                                placeholder="Nordmann"
                                v-bind="componentField"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        </FormField>
                      </div>

                      <FormField v-slot="{ componentField }" name="email">
                        <FormItem>
                          <FormLabel class="text-secondaryBlack">E-post</FormLabel>
                          <FormControl>
                            <Input
                              class="bg-white"
                              type="email"
                              placeholder="Ola.Nordmann@example.no"
                              v-bind="componentField"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      </FormField>
                    </div>
                    <HorizontalLineBesideText> Velg bank </HorizontalLineBesideText>
                    <div class="relative mb-2">
                      <Search
                        class="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"
                        :size="16"
                      />
                      <Input
                        v-model="bankSearch"
                        placeholder="Søk etter bank..."
                        class="pl-8 bg-white"
                      />
                    </div>

                    <div class="max-h-60 overflow-y-auto">
                      <div
                        v-for="bank in filteredBanks"
                        :key="bank.bic"
                        class="p-2 rounded-md hover:bg-gray-100 cursor-pointer"
                        :class="bankPicked === bank.bic ? 'border-primary border bg-white' : ''"
                        @click="bankPicked = bankPicked === bank.bic ? null : bank.bic"
                      >
                        {{ bank.name }}
                      </div>
                      <div v-if="filteredBanks.length === 0" class="text-sm text-gray-400">
                        Ingen banker funnet
                      </div>
                    </div>
                    <DataStateButton
                      :data-state="addConsentMutation.dataState"
                      class="mt-4 text-wrap"
                      size="sm"
                      type="submit"
                      :disabled="!bankPicked"
                    >
                      <ListChecksIcon :size="20" class="inline-block mr-2" /> Velg kontoer i
                      {{ banks?.find((b) => b.bic === bankPicked)?.name ?? 'banken' }}
                    </DataStateButton>
                  </form>
                </div>
              </template>
            </StepperItemCustom>
          </Stepper>
        </div>

        <!-- 2. If we have an active consent, show the details -->
        <div v-else-if="data?.status === 'Active'" class="flex flex-col gap-2 w-full">
          <h2 class="text-lg font-semibold">Bankintegrasjon er aktiv</h2>
          <p class="text-sm text-gray-500 max-w-[65ch]">
            Du har gitt tilgang til følgende bankkontoer:
          </p>
          <div class="flex flex-col gap-2">
            <div
              class="flex gap-4 w-full bg-white p-4 border border-slate-200 rounded-md items-center"
              v-for="consent in data.accounts"
              :key="consent.bban"
            >
              <div class="p-4 rounded-md bg-gray-50 flex items-center justify-center shrink-0 w-16">
                <LandmarkIcon class="text-gray-400 h-full" />
              </div>
              <div class="flex flex-col sm:flex-row gap-2 w-full">
                <div class="rounded-md flex flex-col gap-0">
                  <h3 class="font-semibold">{{ consent.name }}</h3>
                  <span class="text-xs text-muted-foreground">
                    <span class="font-semibold"> Kontonummer: {{ consent.bban }}</span
                    ><br />
                    <span class="font-semibold">{{ consent.bankName }}</span>
                  </span>
                </div>
                <div
                  class="flex items-center gap-1 text-xs text-green-500 sm:ml-auto font-semibold"
                >
                  Tilkoblet
                  <CircleCheck :size="22" class="text-green-600" />
                </div>
              </div>
            </div>
          </div>
          <Button class="self-end" variant="default" @click="goToSuccess"> Gå videre </Button>
        </div>
      </section>
      <!-- </DataStatePage> -->
    </DataStatePage>
  </div>
</template>
